/**
 * Contracts API Service
 *
 * Provides API functions for managing contract templates and contracts.
 */

import { api } from './api';

// ============================================
// TYPES
// ============================================

export type CustomFieldType = 'text' | 'number' | 'date' | 'currency';

export interface CustomField {
  name: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  defaultValue?: string;
}

export interface ContractTemplate {
  _id: string;
  organizationId: string;
  name: string;
  description?: string;
  content: string;
  category?: string;
  customFields: CustomField[];
  boldsignTemplateId?: string;
  isActive: boolean;
  usageCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContractTemplateListItem extends Omit<ContractTemplate, 'content'> {
  // Content is excluded from list responses for performance
}

export interface CreateTemplateParams {
  name: string;
  description?: string;
  content: string;
  category?: string;
  customFields?: CustomField[];
}

export interface UpdateTemplateParams {
  name?: string;
  description?: string;
  content?: string;
  category?: string;
  customFields?: CustomField[];
  isActive?: boolean;
}

export interface MergeField {
  key: string;
  label: string;
  category: 'organization' | 'client' | 'contract' | 'custom';
  description: string;
  example: string;
}

export interface MergeFieldsResponse {
  mergeFields: MergeField[];
  byCategory: Record<string, MergeField[]>;
}

export type ContractStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'revoked';

export interface Contract {
  _id: string;
  organizationId: string;
  contractNumber: string;
  templateId?: string;
  clientId: string;
  name: string;
  content: string;
  customFieldValues: Record<string, string>;
  boldsignDocumentId?: string;
  boldsignStatus?: string;
  status: ContractStatus;
  sentAt?: string;
  viewedAt?: string;
  signedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  expiredAt?: string;
  revokedAt?: string;
  expirationDays: number;
  reminderDays?: number;
  signedDocumentUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  client?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company?: string;
  };
  template?: {
    _id: string;
    name: string;
  };
}

export interface CreateContractParams {
  templateId?: string;
  clientId: string;
  name?: string;
  content?: string;
  customFieldValues?: Record<string, string>;
  expirationDays?: number;
}

export interface UpdateContractParams {
  name?: string;
  content?: string;
  customFieldValues?: Record<string, string>;
  expirationDays?: number;
}

export interface SendContractParams {
  message?: string;
}

export interface ContractListParams {
  status?: ContractStatus | 'all';
  clientId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ContractListResponse {
  contracts: Contract[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ContractStats {
  total: number;
  draft: number;
  sent: number;
  viewed: number;
  signed: number;
  completed: number;
  declined: number;
  expired: number;
  revoked: number;
  pendingCount: number; // sent + viewed (awaiting signature)
}

export interface ContractSettings {
  notifyOnViewed: boolean;
  notifyOnSigned: boolean;
  notifyOnDeclined: boolean;
  notifyOnExpired: boolean;
  defaultExpirationDays: number;
  defaultReminderDays: number;
  autoRemindersEnabled: boolean;
}

export interface UpdateContractSettingsParams {
  notifyOnViewed?: boolean;
  notifyOnSigned?: boolean;
  notifyOnDeclined?: boolean;
  notifyOnExpired?: boolean;
  defaultExpirationDays?: number;
  defaultReminderDays?: number;
  autoRemindersEnabled?: boolean;
}

export interface ContractActivity {
  _id: string;
  contractId: string;
  organizationId: string;
  action: string;
  performedBy?: string;
  performedByType: 'user' | 'system' | 'signer';
  metadata?: {
    signerEmail?: string;
    signerName?: string;
    declineReason?: string;
    ipAddress?: string;
    userAgent?: string;
    documentId?: string;
    fileName?: string;
  };
  createdAt: string;
}

// ============================================
// CONTRACT TEMPLATES API
// ============================================

export const contractTemplatesApi = {
  /**
   * List all templates for the organization
   */
  list: async (params?: {
    search?: string;
    category?: string;
    includeInactive?: boolean;
  }): Promise<{ templates: ContractTemplateListItem[]; total: number }> => {
    const response = await api.get('/contract-templates', { params });
    return response.data.data;
  },

  /**
   * Get a specific template
   */
  get: async (id: string): Promise<ContractTemplate> => {
    const response = await api.get(`/contract-templates/${id}`);
    return response.data.data;
  },

  /**
   * Create a new template
   */
  create: async (params: CreateTemplateParams): Promise<ContractTemplate> => {
    const response = await api.post('/contract-templates', params);
    return response.data.data;
  },

  /**
   * Update a template
   */
  update: async (id: string, params: UpdateTemplateParams): Promise<ContractTemplate> => {
    const response = await api.put(`/contract-templates/${id}`, params);
    return response.data.data;
  },

  /**
   * Delete a template (soft delete)
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/contract-templates/${id}`);
    return response.data.data;
  },

  /**
   * Get available merge fields
   */
  getMergeFields: async (): Promise<MergeFieldsResponse> => {
    const response = await api.get('/contract-templates/merge-fields');
    return response.data.data;
  },
};

// ============================================
// CONTRACTS API
// ============================================

export const contractsApi = {
  /**
   * List contracts for the organization
   */
  list: async (params?: ContractListParams): Promise<ContractListResponse> => {
    const response = await api.get('/contracts', { params });
    return response.data.data;
  },

  /**
   * Get a specific contract
   */
  get: async (id: string): Promise<Contract> => {
    const response = await api.get(`/contracts/${id}`);
    return response.data.data;
  },

  /**
   * Create a new contract (draft)
   */
  create: async (params: CreateContractParams): Promise<Contract> => {
    const response = await api.post('/contracts', params);
    return response.data.data;
  },

  /**
   * Update a contract (draft only)
   */
  update: async (id: string, params: UpdateContractParams): Promise<Contract> => {
    const response = await api.put(`/contracts/${id}`, params);
    return response.data.data;
  },

  /**
   * Delete a contract (draft only)
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/contracts/${id}`);
    return response.data.data;
  },

  /**
   * Send a contract for signature
   */
  send: async (id: string, params?: SendContractParams): Promise<Contract> => {
    const response = await api.post(`/contracts/${id}/send`, params || {});
    return response.data.data;
  },

  /**
   * Revoke/void a contract
   */
  revoke: async (id: string, reason?: string): Promise<{ message: string }> => {
    const response = await api.post(`/contracts/${id}/revoke`, { reason });
    return response.data.data;
  },

  /**
   * Send reminder to signers
   */
  remind: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/contracts/${id}/remind`);
    return response.data.data;
  },

  /**
   * Download signed document
   */
  download: async (id: string): Promise<Blob> => {
    const response = await api.get(`/contracts/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download audit trail
   */
  downloadAudit: async (id: string): Promise<Blob> => {
    const response = await api.get(`/contracts/${id}/audit`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get contract statistics
   */
  getStats: async (): Promise<ContractStats> => {
    const response = await api.get('/contracts/stats');
    return response.data.data;
  },

  /**
   * Get contract settings
   */
  getSettings: async (): Promise<ContractSettings> => {
    const response = await api.get('/contracts/settings');
    return response.data.data;
  },

  /**
   * Update contract settings
   */
  updateSettings: async (params: UpdateContractSettingsParams): Promise<ContractSettings> => {
    const response = await api.patch('/contracts/settings', params);
    return response.data.data;
  },

  /**
   * Get activity timeline for a contract
   */
  getActivities: async (contractId: string): Promise<ContractActivity[]> => {
    const response = await api.get(`/contracts/${contractId}/activities`);
    return response.data.data;
  },

  /**
   * Download signed document from storage
   */
  downloadSignedDocument: async (contractId: string): Promise<Blob> => {
    const response = await api.get(`/contracts/${contractId}/signed-document`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download audit trail PDF
   */
  downloadAuditTrail: async (contractId: string): Promise<Blob> => {
    const response = await api.get(`/contracts/${contractId}/audit-trail`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
